import { useEffect } from 'react';

// Served from `public/`. It controls the whole origin, so it sits at the root.
const SERVICE_WORKER_URL = '/sw.js';

type ServiceWorkers = Pick<ServiceWorkerContainer, 'register'>;

/**
 * Installs the app's `public/sw.js`, from the app's client root so every page
 * has it: what it does is that file's (the worker's offline page, push in the
 * worker and admin apps), and a registered one makes the app installable.
 * `usePushNotifications` reads this registration, never makes its own.
 * Registering again on every load is how the browser checks for a new
 * `sw.js`, and costs nothing when it is unchanged.
 *
 * Best-effort: a browser without service workers, or a failed register, leaves
 * the app as it was. The container is read in the effect, never on the server,
 * and is injectable so a spec can hand in one that fails.
 */
export function useRegisterServiceWorker({
  findServiceWorkers = findBrowserServiceWorkers,
}: {
  findServiceWorkers?: () => ServiceWorkers | undefined;
} = {}) {
  useEffect(() => {
    findServiceWorkers()
      ?.register(SERVICE_WORKER_URL)
      .catch(function ignoreRegisterFailure() {
        // no offline page, no worse than before
      });
  }, [findServiceWorkers]);
}

// Undefined on an insecure origin or an old browser, whatever the DOM types say.
function findBrowserServiceWorkers(): ServiceWorkers | undefined {
  return navigator.serviceWorker;
}
